import { useCallback, useEffect, useRef, useState } from 'react';

import type { SheetAppointment, SheetReport } from '../lib/crm/accountSheet';
import { logWarn } from '../lib/logger';
import { supabase } from '../lib/supabase';
import { readContact } from '../store/crm/contactsSlice';
import type { CRMContact, CRMOpportunity } from '../types/crm';
import type { CRMNote } from '../types/notes';

export interface ContactRows {
  opportunities: CRMOpportunity[];
  notes: CRMNote[];
  appointments: SheetAppointment[];
  reports: SheetReport[];
}

export interface ContactSheetData {
  contact: CRMContact | null;
  /** No such contact: the sheet says so. */
  missing: boolean;
  /** The contact could not be read: the sheet says that, not « introuvable » (beside it on a re-read). */
  error: string | null;
  rows: ContactRows;
  /** The rows are this contact's (never « Aucun… » before they are). */
  rowsReady: boolean;
  rowsError: string | null;
  /** Read the contact again after an edit (its mails, phones, accounts). */
  reload: () => Promise<void>;
}

const NO_ROWS: ContactRows = { opportunities: [], notes: [], appointments: [], reports: [] };

async function readRows(contactId: string): Promise<ContactRows> {
  const [opps, notes, appointments] = await Promise.all([
    supabase
      .from('crm_opportunity_contacts')
      .select('role, opportunity:crm_opportunities(*, stage:crm_pipeline_stages(*))')
      .eq('contact_id', contactId),
    supabase
      .from('crm_notes')
      .select('*')
      .eq('contact_id', contactId)
      .order('created_at', { ascending: false })
      .limit(10),
    supabase
      .from('appointments')
      .select('id, start_time, title, status, appointment_type:appointment_types(name)')
      .eq('contact_id', contactId)
      .order('start_time', { ascending: false })
      .limit(10),
  ]);
  const failed = opps.error ?? notes.error ?? appointments.error;
  if (failed) throw new Error(failed.message);
  // The meeting reports of its appointments and of its notes (as the account's record reads them).
  // Optional: a report that cannot be read leaves the deals and the exchanges shown.
  const appointmentIds = (appointments.data ?? []).map((a) => a.id);
  const noteIds = (notes.data ?? []).map((n) => n.id);
  const filters = [
    appointmentIds.length > 0 ? `appointment_id.in.(${appointmentIds.join(',')})` : null,
    noteIds.length > 0 ? `note_id.in.(${noteIds.join(',')})` : null,
  ].filter((f): f is string => f !== null);
  let reports: SheetReport[] = [];
  if (filters.length > 0) {
    const res = await supabase
      .from('crm_meeting_reports')
      .select('id, cr_title, created_at')
      .or(filters.join(','))
      .order('created_at', { ascending: false })
      .limit(5);
    if (res.error) logWarn('crm', 'contact sheet: meeting reports unreadable', { contactId, error: res.error.message });
    else reports = (res.data ?? []).map((r) => ({ id: r.id, cr_title: r.cr_title, created_at: r.created_at ?? '' }));
  }
  return {
    opportunities: (opps.data ?? []).flatMap((row) => (row.opportunity ? [row.opportunity as unknown as CRMOpportunity] : [])),
    notes: (notes.data ?? []).map((n) => ({ ...n, created_at: n.created_at ?? '' })) as unknown as CRMNote[],
    appointments: (appointments.data ?? []).map((a) => ({
      id: a.id,
      start_time: a.start_time,
      status: a.status,
      // An appointment without a title says its type (« Démo »), as the old sheet did.
      title: a.title?.trim() || (a.appointment_type as { name?: string | null } | null)?.name || null,
    })),
    reports,
  };
}

/**
 * A contact's working sheet (PLAN-ENCRE-OUTILS O2): the contact as the screens read it
 * (`readContact`: mails, phones, linked accounts), then its deals, notes, appointments and meeting
 * reports. Every answer belongs to ONE contact: a late one for a contact left since is dropped.
 */
export function useContactSheet(contactId: string | null): ContactSheetData {
  const [contact, setContact] = useState<CRMContact | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ContactRows>(NO_ROWS);
  const [rowsFor, setRowsFor] = useState<string | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const latest = useRef<string | null>(null);
  // Each read of the contact is numbered: an older one arriving last is dropped (two quick edits).
  const readCount = useRef(0);

  const loadContact = useCallback(async (id: string) => {
    const ticket = ++readCount.current;
    const read = await readContact(id);
    if (latest.current !== id || ticket !== readCount.current) return;
    if (read.error) logWarn('crm', 'contact sheet: contact unreadable', { contactId: id, error: read.error });
    // A failed RE-read keeps the contact shown (and the form open on it): the error is said beside it.
    setContact((shown) => (read.error && shown?.id === id ? shown : read.contact));
    setError(read.error);
    setMissing(!read.error && !read.contact);
  }, []);

  useEffect(() => {
    latest.current = contactId;
    setContact(null);
    setMissing(false);
    setError(null);
    setRows(NO_ROWS);
    setRowsFor(null);
    setRowsError(null);
    if (!contactId) return;
    void loadContact(contactId);
    readRows(contactId)
      .then((read) => {
        if (latest.current !== contactId) return;
        setRows(read);
        setRowsFor(contactId);
      })
      .catch((err: unknown) => {
        if (latest.current !== contactId) return;
        const message = err instanceof Error ? err.message : String(err);
        logWarn('crm', 'contact sheet: rows unreadable', { contactId, error: message });
        setRowsError(message);
      });
  }, [contactId, loadContact]);

  const reload = useCallback(async () => {
    if (contactId) await loadContact(contactId);
  }, [contactId, loadContact]);

  return { contact, missing, error, rows, rowsReady: contactId !== null && rowsFor === contactId, rowsError, reload };
}
